import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { InvoicePreview } from '@/components/InvoicePreview';
import type { CompanySettings } from '@/types/firebase';

export default function InvoicePaymentPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<any>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInvoiceDirectly() {
      try {
        // Try multiple approaches to find the invoice
        let invoiceData = null;
        
        // 1. Try document ID lookup
        try {
          const docRef = doc(db, 'invoices', invoiceId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            invoiceData = { id: docSnap.id, ...docSnap.data() };
          }
        } catch (err) {
          // Doc lookup error
        }
        
        // 2. Try invoice number lookup
        if (!invoiceData) {
          try {
            const q = query(
              collection(db, 'invoices'), 
              where('invoiceNumber', '==', invoiceId)
            );
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              invoiceData = { id: doc.id, ...doc.data() };
            }
          } catch (err) {
            // Invoice number lookup error
          }
        }
        
        // 3. Try payment key lookup
        if (!invoiceData) {
          try {
            const q = query(
              collection(db, 'invoices'), 
              where('paymentKey', '==', invoiceId)
            );
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              invoiceData = { id: doc.id, ...doc.data() };
            }
          } catch (err) {
            // Payment key lookup error
          }
        }
        
        if (invoiceData) {
          setInvoice(invoiceData);
          
          // Now fetch the company settings
          try {
            const companyId = invoiceData.companyId;
            
            const companyDocRef = doc(db, 'companies', companyId);
            const companyDocSnap = await getDoc(companyDocRef);
            
            if (companyDocSnap.exists()) {
              const companyData = companyDocSnap.data() as CompanySettings;
              setCompanySettings(companyData);
            } else {
              throw new Error('Company not found');
            }
          } catch (companyErr) {
            // Company settings error
            throw new Error('Could not load company settings');
          }
        } else {
          throw new Error('Invoice not found');
        }
        
        setLoading(false);
      } catch (err) {
        // Error fetching invoice
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    }

    fetchInvoiceDirectly();
  }, [invoiceId]);

  return (
    <div className="container mx-auto py-8 px-4">
      {loading && (
        <div className="text-center mb-6">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4">Loading invoice {invoiceId}...</p>
        </div>
      )}
      
      {error && (
        <Card className="p-6 bg-red-50 border-red-200 mb-6">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Invoice</h2>
          <p className="text-gray-700">{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </Card>
      )}
      
      {invoice && companySettings && (
        <div className="max-w-5xl mx-auto space-y-8">
          <Card>
            <CardContent className="p-6">
              <InvoicePreview
                invoice={invoice}
                companySettings={companySettings}
                className="w-full"
                showPreviewWatermark={false}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
} 